import ReactEcharts from 'echarts-for-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BasicSelectionBox,
  BasicTable,
  ContentCard,
} from '@/components/customComponents'

import { lineChartOptions } from '@/constants/CHART_CONSTANTS'
import { toast, useAllSkus, usePriceHistory } from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { PriceHistoryMonthlyModel } from '@/types/clarityLens'
import { dateToString } from '@/utils'

interface PriceHistoryTabProps {
  itemName: string
}

export const PriceHistoryTab = ({
  itemName: itemName,
}: PriceHistoryTabProps) => {
  const { t } = useTranslation()
  // Get filter state from Redux
  const { selectedBranch, date } = useReduxBaseFilter('spend-lens-item')
  const [averageRccPriceFilter, setAverageRccPriceFilter] =
    useState<string>('ptiMoneyImpact')
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('spend-lens-item')
  )

  const {
    seasonalityChartData,
    loading: priceHistoryLoading,
    error: priceHistoryError,
    refetch: priceHistoryRefetch,
  } = usePriceHistory(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName,
    averageRccPriceFilter
  )

  const {
    allSkusData,
    allSkusColumns,
    loading: allSkusLoading,
    error: allSkusError,
    refetch: allSkusRefetch,
  } = useAllSkus(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing price history data...')
      priceHistoryRefetch()
      allSkusRefetch()
    }
  }, [refreshTrigger, priceHistoryRefetch, allSkusRefetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (priceHistoryError || allSkusError) {
      toast({
        title: t('common.errors.serverError'),
        description: priceHistoryError?.message || allSkusError?.message,
        variant: 'destructive',
      })
    }
  }, [priceHistoryError, allSkusError])

  return (
    <div className="space-y-6">
      <div>
        <ContentCard
          title={t('clarityLens.spendLens.charts.averageMaterialCodePrice')}
          loading={priceHistoryLoading}
          isEmpty={!seasonalityChartData}
          customButton={
            <BasicSelectionBox
              options={[
                { value: 'ptiMoneyImpact', label: 'PTI $ Impact' },
                { value: 'ptiPercentage', label: 'PTI %' },
                { value: 'ptiVsCti', label: 'PTI % vs CTI %' },
              ]}
              onValueChange={setAverageRccPriceFilter}
              defaultValue={averageRccPriceFilter || 'ptiMoneyImpact'}
              className="w-[160px]"
            />
          }
        >
          {seasonalityChartData && (
            <ReactEcharts
              option={getLineChartData(
                averageRccPriceFilter,
                seasonalityChartData?.data
              )}
            />
          )}
        </ContentCard>
      </div>
      <div>
        <BasicTable
          title={t('clarityLens.spendLens.charts.allSkus')}
          data={allSkusData}
          columns={allSkusColumns}
          loading={allSkusLoading}
          enablePagination
        />
      </div>
    </div>
  )
}

const getLineChartData = (metric: string, data?: PriceHistoryMonthlyModel) => {
  const metricType = metric === 'ptiMoneyImpact' ? 'money' : 'percentage'

  // Safety check for empty data
  if (!data || Object.keys(data).length === 0) {
    return lineChartOptions([], [], [], metricType)
  }

  // Extract month names and values from PriceHistoryMonthlyModel
  const monthNames = Object.keys(data) as Array<keyof PriceHistoryMonthlyModel>
  const values = monthNames.map((month) => data[month])

  // Safety check for no months found
  if (monthNames.length === 0) {
    return lineChartOptions([], [], [], metricType)
  }

  // Create raw data as a single series
  const rawData = [values]
  const seriesNames = ['Price History']

  return lineChartOptions(rawData, seriesNames, monthNames, metricType)
}
